import styled, { keyframes } from "styled-components";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../../utils/constants";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// A composited transform, never an animated width, so the entrance never triggers layout.
const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--color-background-0);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
`;

export const HeroSkeletonWrapper = styled.div`
  background: var(--color-card-background);
  border: 1px solid var(--color-border);
  border-radius: ${SPACING.md}px;
  box-shadow: var(--shadow-large);
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  margin: ${SPACING_RESPONSIVE.mobile.chartMargin}px
    ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
    margin: ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    margin-bottom: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    margin: ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    margin-bottom: ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }
`;

export const FiltersSkeletonWrapper = styled.div`
  margin: 0 ${SPACING_RESPONSIVE.mobile.chartMargin}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.chartMargin}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm + SPACING.xs}px;
  align-items: flex-start;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    margin-bottom: 0px;
    flex-direction: row;
    align-items: center;
    gap: ${SPACING.sm + SPACING.xs}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    margin-bottom: 0px;
  }
`;

export const InsightsSkeletonWrapper = styled.div`
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }
`;

export const HoldingsSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-top: 0;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-top: 0;
  }
`;

export const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "32px"};
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: loading 1.5s ${ANIMATION.easing.easeInOut} infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const segmentColor = {
  principal: "var(--color-success)",
  interest: "var(--color-warning)",
} as const;

export const HeroCard = styled.section`
  background: var(--hero-background);
  border: 1px solid var(--color-border);
  border-radius: ${SPACING.md}px;
  box-shadow: var(--shadow-large);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  column-gap: ${SPACING.lg}px;
  row-gap: ${SPACING.md}px;
  grid-template-areas:
    "primary primary"
    "donut debtfree"
    "where where";

  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  margin: ${SPACING_RESPONSIVE.mobile.chartMargin}px
    ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
    margin: ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    margin-bottom: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    grid-template-columns: auto 2fr 1fr;
    column-gap: ${SPACING.xl}px;
    grid-template-areas:
      "topbar  topbar   topbar"
      "donut   primary  debtfree"
      "donut   divider  divider"
      "donut   where    where";
  }

  @media ${QUERIES.laptopLargeAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    margin: ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    margin-bottom: ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }
`;

export const TopBar = styled.div`
  grid-area: topbar;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const HeroLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const DonutBlock = styled.div`
  grid-area: donut;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  align-self: stretch;

  @media ${QUERIES.tabletLargeAndUp} {
    margin: 0 ${SPACING.xl}px;
    justify-self: start;
  }
`;

export const DonutOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

export const DonutPercent = styled.span`
  font-size: var(--text-xl);
  font-weight: 700;
  line-height: 1;
  color: var(--color-text-default);

  @media ${QUERIES.tabletLargeAndUp} {
    font-size: var(--text-xxl);
  }
`;

export const DonutCaption = styled.span`
  margin-top: ${SPACING.xs}px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

export const PrimaryBlock = styled.div`
  grid-area: primary;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const DebtRemainingValue = styled.span`
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--color-text-default);

  @media ${QUERIES.tabletLargeAndUp} {
    font-size: var(--text-display-lg);
  }
`;

export const DebtFreeBlock = styled.div`
  grid-area: debtfree;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  justify-self: flex-start;
  text-align: left;
`;

export const DebtFreeYear = styled.span`
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: var(--color-primary);
`;

export const DebtFreeNote = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const HeroDivider = styled.div`
  grid-area: divider;
  width: 100%;
  height: 1px;
  background: var(--color-border);
`;

export const WhereItWent = styled.div`
  grid-area: where;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

// Negative margins cancel the card padding so the band runs full-bleed along the card's bottom edge.
export const PaidSoFarBand = styled.div`
  grid-area: where;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  background: var(--hero-footer-background);
  border-top: 1px solid var(--color-border);
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  margin: 0 -${SPACING_RESPONSIVE.mobile.cardPadding}px -${
      SPACING_RESPONSIVE.mobile.cardPadding
    }px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;

  @media ${QUERIES.tabletAndUp} {
    margin: 0 -${SPACING_RESPONSIVE.tablet.cardPadding}px -${
        SPACING_RESPONSIVE.tablet.cardPadding
      }px;
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }
`;

export const PaidSoFarHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  margin: 0 ${SPACING_RESPONSIVE.mobile.pageMargin}px;
`;

export const PaidSoFarTotal = styled.span`
  font-size: var(--text-xl);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
`;

export const CompactLegend = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  margin: 0 ${SPACING_RESPONSIVE.mobile.pageMargin}px;
`;

export const BarTrack = styled.div`
  display: flex;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--color-border);
`;

export const BarReveal = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const BarSegment = styled.div<{
  $ratio: number;
  $tone: keyof typeof segmentColor;
}>`
  width: ${(props) => props.$ratio * 100}%;
  background: ${(props) => segmentColor[props.$tone]};
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.xl}px;
`;

export const LegendItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const LegendDivider = styled.div`
  align-self: stretch;
  width: 1px;
  background: var(--color-border);
`;

export const LegendHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

export const LegendSwatch = styled.span<{ $tone: keyof typeof segmentColor }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(props) => segmentColor[props.$tone]};
`;

export const LegendValue = styled.span`
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-default);
`;
